import { api } from './client';
import type { ReferencesReport } from './persons';
import type {
  AccountInsurance, AccountInsuranceInput, LifecycleStatus,
  MoveFootprint, MoveRequest, MoveResponse,
  Page, UUID, WriteResult,
} from './types';

export interface AccountInsuranceListParams {
  account_id?: UUID; agreement_id?: UUID; person_id?: UUID;
  /** #277 (2026-06-08): multi-status filter via repeated query string. */
  status?: LifecycleStatus[];
  /** #274 (2026-06-08): limbo Policies (agreement_id IS NULL).
   *  Mutually exclusive with agreement_id. Powers the
   *  "Attach existing Policy" chooser. */
  unassigned_only?: boolean;
  q?: string;
  include_deleted?: boolean; limit?: number; offset?: number;
  // #46 Wave 1 (2026-06-12): per-column predicate filters (C-132).
  insurer_q?: string;
  policy_q?: string;
  agreement_q?: string;
  /** #46 follow-up (2026-06-12): substring match on any alive Indirect
   *  Account owned by the policy (bank / internal # / IBAN). */
  account_q?: string;
  /** P41: a column key, '-' prefix to reverse (server-side). */
  sort?: string;
}
function qs(p: object): string {
  const u = new URLSearchParams();
  for (const [k, v] of Object.entries(p)) {
    if (v === undefined || v === null || v === '') continue;
    // #277 (2026-06-08): arrays expand to repeated query keys.
    if (Array.isArray(v)) {
      for (const item of v) {
        if (item !== undefined && item !== null && item !== '') {
          u.append(k, String(item));
        }
      }
    } else {
      u.set(k, String(v));
    }
  }
  return u.toString() ? `?${u}` : '';
}

/** The Insurance Policies list's "Export CSV" (Pedro 2026-10-10; Admin-only, C-335): the
 * same filters as the list, without paging — the export mirrors the screen. */
export function accountInsuranceExportHref(params: AccountInsuranceListParams): string {
  const { limit: _l, offset: _o, ...filters } = params;
  return `/account-insurance/export.xlsx${qs(filters)}`;
}

async function writeWithMaker<T>(
  call: () => Promise<{ data: T; status: number; headers: Headers }>,
): Promise<WriteResult<T>> {
  const r = await call();
  return {
    entity: r.data,
    pending: r.headers.get('X-Change-Request-Status') === 'pending',
    changeRequestId: r.headers.get('X-Change-Request-Id'),
  };
}

export const accountInsuranceApi = {
  list:    (p: AccountInsuranceListParams = {}) => api.get<Page<AccountInsurance>>(`/account-insurance${qs(p)}`),
  get:     (id: UUID, includeDeleted = false) =>
    api.get<AccountInsurance>(`/account-insurance/${id}${includeDeleted ? '?include_deleted=true' : ''}`),
  create:  (payload: AccountInsuranceInput)      => writeWithMaker(() => api.postMeta<AccountInsurance>('/account-insurance', payload)),
  update:  (id: UUID, p: AccountInsuranceInput)  => writeWithMaker(() => api.patchMeta<AccountInsurance>(`/account-insurance/${id}`, p)),
  delete:  (id: UUID)                            => writeWithMaker(() => api.deleteMeta<AccountInsurance>(`/account-insurance/${id}`)),
  restore: (id: UUID)                            => writeWithMaker(() => api.postMeta<AccountInsurance>(`/account-insurance/${id}/restore`)),
  references:    (id: UUID) => api.get<ReferencesReport>(`/account-insurance/${id}/references`),
  cascadeDelete: (id: UUID, refIds: Record<string, UUID[]>) =>
    writeWithMaker(() => api.postMeta<AccountInsurance>(`/account-insurance/${id}/cascade-delete`, { ref_ids: refIds })),

  /** #272 (2026-06-08): Detach an Active Policy from its Agreement.
   *  Server-side: Policy → Inactive limbo + cascade Indirect Accounts to Inactive.
   *  #26 (2026-06-10): maker-checker — WriteResult shape; RM filings come
   *  back .pending with a `_detach_plan` footprint CR. */
  detach: (id: UUID) =>
    writeWithMaker(() => api.postMeta<AccountInsurance>(`/account-insurance/${id}/detach`)),
  /** #272 (2026-06-08): Attach a limbo (Inactive, agreement_id IS NULL)
   *  Policy to a target Active Agreement.  Asymmetric reversal — Indirect
   *  Accounts owned by this Policy are NOT auto-resumed.
   *  2026-06-10 (Fable-5 #18 smoke): endpoint converted to maker-checker —
   *  WriteResult shape so RM filings surface as .pending. */
  attach: (id: UUID, agreementId: UUID) =>
    writeWithMaker(() => api.postMeta<AccountInsurance>(
      `/account-insurance/${id}/attach`, { agreement_id: agreementId },
    )),

  /** #376 → #388 (2026-06-08 → renamed 2026-06-09): attach an orphan Account
   *  (agreement_id NULL + opened_by_policy_id NULL + status=Inactive) to an
   *  existing Active Policy as Indirect. Server-side promotes the account
   *  to Active and stamps opened_by_policy_id. Returns the refreshed
   *  AccountInsurance (the Policy itself; .account_ids reflects the new link).
   *
   *  Naming note. Before 2026-06-09 the same call lived in a separate file
   *  `api/insurancePolicyAccounts.ts` as `insurancePolicyAccountsApi.create`
   *  — a thin shim left over from the pre-0028 M:N join table. The #388
   *  cleanup moved the method here (attachAccount is the natural sibling
   *  of attach + detach above) and deleted the shim. */
  attachAccount: (policyId: UUID, accountId: UUID) =>
    writeWithMaker(() => api.postMeta<AccountInsurance>(
      `/account-insurance/${policyId}/accounts/attach`,
      { account_id: accountId },
    )),

  /** #322 (2026-06-08): Terminate an Active Policy. Three flavours, same
   *  shape — server stamps close_reason atomically with the status
   *  transition (Active → Closed). Active Indirect Accounts cascade to
   *  Closed (terminal — children terminate with the parent).
   *  Already-Inactive Indirect Accounts stay Inactive.
   *  Terminal — cannot be reversed via Attach.
   *  #26 (2026-06-10): maker-checker — WriteResult shape; RM filings come
   *  back .pending with a `_terminate_plan` footprint CR. */
  redeem: (id: UUID) =>
    writeWithMaker(() => api.postMeta<AccountInsurance>(`/account-insurance/${id}/redeem`)),
  lapse: (id: UUID) =>
    writeWithMaker(() => api.postMeta<AccountInsurance>(`/account-insurance/${id}/lapse`)),
  mature: (id: UUID) =>
    writeWithMaker(() => api.postMeta<AccountInsurance>(`/account-insurance/${id}/mature`)),

  /** #104 Wave B (2026-06-08): preview the footprint of a move.
   *  Returns source + target Agreement labels and the list of Active
   *  Indirect Accounts that would cascade to Inactive. Backend rejects
   *  preconditions here (Closed source → 400, target Inactive → 400,
   *  source == target → 400). */
  movePreview: (id: UUID, targetAgreementId: UUID) =>
    api.get<MoveFootprint>(
      `/account-insurance/${id}/move-preview?target_agreement_id=${targetAgreementId}`,
    ),
  /** #104 Wave B (2026-06-08): apply the move via maker-checker.
   *  Compliance/Admin auto-apply (200). RM filings queue a pending CR
   *  (202 with X-Change-Request-* headers — caller can inspect
   *  body.change_request_status to distinguish 'applied' vs 'pending').
   *  Footprint mismatch returns 409 — caller re-fetches the preview. */
  move: (id: UUID, body: MoveRequest) =>
    api.postMeta<MoveResponse>(`/account-insurance/${id}/move`, body),
};
